import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { getProjectsByService } from "../data/projects";

const DeveloppementWeb = () => {
  const services = [
    {
      num: "01",
      title: "Progressive Web Apps",
      description: "Applications web progressives offrant une expérience native sur tous les appareils.",
      features: ["Mode hors ligne", "Notifications push", "Installation sur l'écran d'accueil"],
    },
    {
      num: "02",
      title: "E-commerce",
      description: "Solutions complètes de vente en ligne avec paiement sécurisé et gestion des stocks.",
      features: ["Paiement sécurisé", "Gestion des commandes", "Tableaux de bord analytics"],
    },
    {
      num: "03",
      title: "CMS Personnalisés",
      description: "Systèmes de gestion de contenu adaptés à vos besoins spécifiques.",
      features: ["Interface intuitive", "Gestion multi-utilisateurs", "Workflows personnalisés"],
    },
    {
      num: "04",
      title: "API REST/GraphQL",
      description: "Conception et développement d'APIs robustes et documentées.",
      features: ["Architecture scalable", "Documentation automatique", "Tests automatisés"],
    },
  ];

  const projects = getProjectsByService("developpement-web");

  return (
    <div className="bg-gray-950 text-white min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-end pb-20">
        <div className="absolute inset-0">
          <img
            src="/images/dev-web/87456.jpg"
            alt="Développement Web"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/70 to-gray-950/30" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 w-full">
          <div className="max-w-3xl">
            <div className="inline-block px-4 py-1 border border-flowkraft-primary/30 text-flowkraft-secondary text-sm mb-6">
              Services
            </div>
            <h1 className="font-display text-display text-white mb-6">
              Développement Web
            </h1>
            <p className="text-xl text-gray-300 max-w-xl">
              Applications web modernes et performantes avec les technologies de pointe
            </p>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-20 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-display text-title text-white mb-6">
                Notre Expertise
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-6">
                Nous concevons et développons des applications web sur mesure qui
                répondent parfaitement à vos besoins métier. Notre équipe maîtrise
                les dernières technologies pour créer des solutions robustes,
                scalables et performantes.
              </p>
              <p className="text-gray-400 text-lg leading-relaxed">
                De la simple vitrine au SaaS complexe, nous accompagnons votre
                projet de la conception à la mise en production et au-delà.
              </p>
            </div>
            <div className="relative">
              <img
                src="/images/dev-web/2150170149.jpg"
                alt="Web Development"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute inset-0 border border-white/10" />
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-display text-title text-white mb-16">
            Nos Services
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="border border-white/10 p-8 hover:border-flowkraft-primary/50 transition-colors"
              >
                <span className="font-display text-4xl text-flowkraft-primary/30 mb-4 block">
                  {service.num}
                </span>
                <h3 className="font-display text-2xl text-white mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-400 mb-6">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center text-gray-300 text-sm">
                      <Check className="w-4 h-4 text-flowkraft-secondary mr-3" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects */}
      <section className="py-20 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-display text-title text-white mb-16">
            Nos Réalisations
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            {projects.map((project, index) => (
              <div key={index} className="group relative overflow-hidden aspect-[4/3] bg-gray-900">
                <img
                  src={project.image}
                  alt={project.title}
                  onError={(e) => {
                    e.currentTarget.style.visibility = "hidden";
                  }}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/50 to-transparent" />
                <div className="absolute inset-0 p-6 flex flex-col justify-end">
                  <h3 className="font-display text-xl text-white mb-2">
                    {project.title}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">
                    {project.summary}
                  </p>
                  {project.link && (
                    <a
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-flowkraft-secondary text-sm font-medium"
                    >
                      Voir le projet
                      <ArrowUpRight className="w-4 h-4 ml-1" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative">
        <div className="absolute inset-0 opacity-10">
          <img
            src="/images/main/cta_background_digital.png"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 text-center">
          <h2 className="font-display text-title text-white mb-6">
            Prêt à démarrer votre projet web ?
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-xl mx-auto">
            Contactez-nous pour discuter de vos besoins
          </p>
          <Link
            to="/#contact"
            className="btn-solid inline-flex"
          >
            Demander un devis
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DeveloppementWeb;
